import { useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { BaseUrlContext } from '@core/context';
import useEnvInfo from '@core/hooks/useEnvInfo';

/**
 * Helper function to get the markdown version of the current URL.
 * Uses React Router's useLocation hook to reactively track route changes,
 * and combines with BaseUrlContext to get the full absolute pathname
 * (including any base paths or API prefixes like /datamarketplace-buyer-api/).
 */
export function useMarkdownUrl() {
  const { isServer } = useEnvInfo();
  const location = useLocation();
  const baseUrl = useContext(BaseUrlContext);

  if (isServer) return '';

  const currentPath = baseUrl === '/' ? location.pathname : `${baseUrl}${location.pathname}`;
  const pathSegments = currentPath.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1] || '';
  const hasFilenameExtension = lastSegment.includes('.');

  // Handle different URL patterns more robustly
  let mdPath = '';
  if (currentPath.endsWith('/')) {
    mdPath = `${currentPath}index.md`;
  } else if (hasFilenameExtension) {
    // Replace existing extension with .md
    mdPath = currentPath.replace(/\.[^/.]+$/, '.md');
  } else {
    // No extension and not trailing slash, just append .md
    mdPath = `${currentPath}.md`;
  }

  return `${window.location.origin}${mdPath}`;
}

/**
 * Returns a function that generates a prompt for a given AI agent bot with the
 * current markdown URL as context.
 */
export function useMarkdownUrlPrompt() {
  const markdownUrl = useMarkdownUrl();

  return useCallback(
    (bot: 'ChatGPT' | 'Claude' | 'Copilot') => {
      return encodeURIComponent(
        `Hi ${bot}! Can you please read [this page](${markdownUrl}) and prepare to answer questions about it?`,
      );
    },
    [markdownUrl],
  );
}
